import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiHardDrive, FiLink, FiRotateCcw, FiSmartphone, FiHeadphones, FiZap } from 'react-icons/fi';
import PageHeader from '../components/PageHeader';
import { redcloudPlans } from '../data/redcloudPlans';

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;
  z-index: 2;
`;

const Card = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const Hero = styled(Card)`
  padding: 2.25rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  line-height: 1.15;
  margin-bottom: 0.9rem;

  @media (max-width: 768px) {
    font-size: 2.1rem;
  }
`;

const HeroSub = styled.p`
  font-size: 1.15rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  max-width: 980px;
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`;

const PrimaryCTA = styled.a`
  background: #ff3333;
  color: white;
  text-decoration: none;
  padding: 0.85rem 1.4rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  transition: background 0.2s;

  &:hover {
    background: #ff4444;
  }
`;

const SecondaryCTA = styled(Link)`
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  text-decoration: none;
  padding: 0.85rem 1.4rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.45);
  }
`;

const OptionalCTA = styled(Link)`
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  text-decoration: none;
  padding: 0.85rem 1.4rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.45);
  }
`;

const BadgeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.12);

  .icon {
    color: #ff3333;
    flex: 0 0 auto;
  }

  .label {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.92);
    line-height: 1.2;
  }
`;

const Section = styled.section`
  margin-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  margin-bottom: 0.75rem;

  span {
    color: #ff3333;
  }
`;

const BodyText = styled.p`
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CompetitorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const CompetitorCard = styled(Card)`
  padding: 1.25rem;

  h3 {
    margin: 0 0 0.5rem;
    color: #ff3333;
    font-size: 1.15rem;
  }

  .row {
    margin-top: 0.75rem;
  }

  .label {
    font-size: 0.85rem;
    opacity: 0.75;
    margin-bottom: 0.25rem;
  }

  .value {
    font-size: 0.95rem;
    line-height: 1.45;
    opacity: 0.92;
  }
`;

const FitPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  font-size: 0.85rem;
  margin-top: 0.75rem;

  span {
    color: #ff3333;
    font-weight: 700;
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ComparisonCard = styled(Card)`
  padding: 1.25rem;

  h3 {
    margin: 0 0 0.25rem;
    font-size: 1.15rem;
  }

  .sub {
    margin: 0 0 0.75rem;
    font-size: 0.95rem;
    opacity: 0.85;
    line-height: 1.45;
  }

  .row {
    margin-top: 0.75rem;
  }

  .label {
    font-size: 0.85rem;
    opacity: 0.75;
    margin-bottom: 0.2rem;
  }

  .value {
    font-size: 0.95rem;
    line-height: 1.45;
    opacity: 0.92;
  }
`;

const Disclaimer = styled.div`
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
`;

const BulletGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const BulletCard = styled(Card)`
  padding: 1.25rem;

  h3 {
    margin: 0 0 0.5rem;
    color: #ff3333;
    font-size: 1.1rem;
  }

  p {
    margin: 0;
    line-height: 1.6;
    opacity: 0.9;
  }
`;

const Callout = styled(Card)`
  border: 1px solid rgba(255, 51, 51, 0.35);

  h3 {
    color: #ff3333;
    margin: 0 0 0.5rem;
  }

  p {
    margin: 0;
    line-height: 1.6;
    opacity: 0.92;
  }
`;

const UseCaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const UseCaseCard = styled(Card)`
  padding: 1.25rem;

  h3 {
    color: #ff3333;
    margin: 0 0 0.5rem;
  }

  .meta {
    font-size: 0.95rem;
    opacity: 0.9;
    line-height: 1.55;
  }

  .tier {
    margin-top: 0.75rem;
    font-weight: 700;
  }

  .tier span {
    color: #ff3333;
  }
`;

const FAQWrap = styled.div`
  margin-top: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const FAQItem = styled.details`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);

  summary {
    cursor: pointer;
    font-weight: 700;
    outline: none;
  }

  p {
    margin-top: 0.75rem;
    line-height: 1.65;
    opacity: 0.9;
  }
`;

const FinalCTA = styled(Card)`
  padding: 2rem;
  margin-top: 2rem;
  text-align: center;

  h2 {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }

  p {
    margin: 0 auto;
    max-width: 820px;
    opacity: 0.9;
    line-height: 1.6;
  }
`;

const CloudHighlight = styled.span`
  color: #ff3333;
`;

function renderCloudText(text) {
  if (typeof text !== 'string' || text.length === 0) return text;

  const parts = text.split(/(Cloud)/g);
  return parts.map((part, idx) => (
    part === 'Cloud'
      ? (
        idx > 0 && typeof parts[idx - 1] === 'string' && parts[idx - 1].endsWith('i')
          ? <React.Fragment key={idx}>{part}</React.Fragment>
          : <CloudHighlight key={idx}>{part}</CloudHighlight>
      )
      : <React.Fragment key={idx}>{part}</React.Fragment>
  ));
}

function renderCloudChildren(node) {
  if (node === null || node === undefined || typeof node === 'boolean') return node;
  if (typeof node === 'string') return renderCloudText(node);
  if (typeof node === 'number') return node;

  if (Array.isArray(node)) {
    return node.map((child, idx) => (
      <React.Fragment key={idx}>{renderCloudChildren(child)}</React.Fragment>
    ));
  }

  if (React.isValidElement(node)) {
    if (node.type === 'code') {
      return node;
    }

    const children = renderCloudChildren(node.props.children);
    return React.cloneElement(node, { ...node.props, children });
  }

  return node;
}

const CloudText = ({ children }) => (
  <>{renderCloudChildren(children)}</>
);

function WhyRedCloud() {
  const metaTitle = 'Why Choose RedCloud Storage | Built for Multi-TB Creator Libraries';
  const metaDescription = 'RedCloud Storage is built for creators who outgrow consumer cloud plans—high-capacity storage (up to 50TB), professional sharing, version control, mobile access, and real human support.';
  const ogTitle = 'Why Choose RedCloud Storage';
  const ogDescription = 'Built for creators who don’t live in gigabytes: storage up to 50TB, sharing, version control, mobile access, and real support.';
  const canonicalUrl = 'https://redcloudstorage.net/why-redcloud';

  const competitors = [
    {
      name: 'Google Drive / Google One',
      bestFor: 'Everyday documents, collaboration, and light-to-medium storage for teams.',
      notIdealFor: 'Single-account creator archives that keep growing without jumping into business tooling.',
      fit: 'Medium'
    },
    {
      name: 'iCloud+',
      bestFor: 'Apple-first workflows and seamless device backups/photo sync.',
      notIdealFor: 'Large multi-TB creator libraries that need granular sharing controls and project organization across platforms.',
      fit: 'Medium'
    },
    {
      name: 'Dropbox',
      bestFor: 'Sync + collaboration with a polished sharing experience for small teams.',
      notIdealFor: 'Cost-effective long-term creator archives when you want everything in one plan tier.',
      fit: 'Medium'
    },
    {
      name: 'Gallery platforms',
      bestFor: 'Client delivery, presentation, proofing, and curated galleries.',
      notIdealFor: 'Long-term master archives and multi-terabyte storage where you want controlled sharing plus project organization.',
      fit: 'Medium'
    }
  ];

  const redcloudPlanMeta = {
    starter: {
      bestFor: 'Serious creators starting with multi-TB storage',
      notes: 'Includes premium support + pro workflow features; designed to scale up when you hit 10TB.'
    },
    growth: {
      bestFor: 'Creators with real libraries (weddings, shoots, projects)',
      notes: 'Multi-TB sweet spot for archive + active work; professional sharing + version restore.'
    },
    studio: {
      bestFor: 'Studios with multiple creators or multi-year retention',
      notes: 'Designed for master archives and predictable budgeting.'
    },
    pro: {
      bestFor: 'Large teams and truly massive libraries',
      notes: 'High-capacity tier for long-term retention + controlled sharing.'
    }
  };

  const formatUsd = (value) => `$${Number(value).toFixed(2)}`;
  const redcloudRows = redcloudPlans.map((p) => {
    const perTb = p.storageTb > 0 ? (p.monthlyPriceUsd / p.storageTb) : 0;
    const meta = redcloudPlanMeta[p.key] || {};

    return {
      provider: `RedCloud ${p.name}`,
      tier: `${p.storageTb}TB for ${formatUsd(p.monthlyPriceUsd)}/mo`,
      price: `${formatUsd(p.monthlyPriceUsd)}/mo`,
      perTb: `≈ ${formatUsd(perTb)}/TB`,
      bestFor: meta.bestFor || 'Creator storage',
      notes: meta.notes || ''
    };
  });

  const rows = [
    {
      provider: 'Google One',
      tier: '2TB for ~$9.99/mo',
      price: '~$9.99/mo',
      perTb: '~$5/TB',
      bestFor: 'Light storage + Google ecosystem',
      notes: 'Great UX; higher tiers can be limited for truly massive single-library workflows.'
    },
    {
      provider: 'iCloud+',
      tier: '2TB ~$9.99/mo; 6TB ~$29.99/mo; 12TB ~$59.99/mo',
      price: '~$9.99–$59.99/mo',
      perTb: '~$5/TB',
      bestFor: 'Apple device sync and backups',
      notes: 'Strong for Apple-centric users; may not match pro sharing + project workflows.'
    },
    {
      provider: 'Dropbox',
      tier: '2TB ~$9.99/mo; 3TB ~$16.58/mo',
      price: '~$9.99–$16.58/mo',
      perTb: '~$5–$5.53/TB',
      bestFor: 'Sharing + sync for teams',
      notes: 'Excellent sharing; scaling to very large storage can get expensive depending on plan path.'
    },
    {
      provider: 'Gallery platforms',
      tier: 'Usually optimized for delivery and presentation',
      price: 'Varies',
      perTb: 'Varies',
      bestFor: 'Galleries, proofing, client presentation',
      notes: 'Not intended as archive storage; upload/RAW constraints may apply.'
    },
    ...redcloudRows
  ];

  return (
    <Page>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <ContentContainer>
        <CloudText>
          <Hero>
            <HeroTitle>Built for creators who don’t live in gigabytes.</HeroTitle>
            <HeroSub>
              RedCloud Storage is a high-capacity cloud storage & sharing platform designed for creators who outgrow consumer plans.
              Get scalable storage solutions (up to 50 terabytes), professional sharing, version control, mobile access, and real human support.
            </HeroSub>

            <CTAGroup>
              <PrimaryCTA href="https://my.redcloudstorage.net" target="_blank" rel="noopener noreferrer">
                Start Free Trial
              </PrimaryCTA>
              <SecondaryCTA to="/pricing">View Pricing</SecondaryCTA>
              <OptionalCTA to="/contact">Contact Sales</OptionalCTA>
            </CTAGroup>

            <HeroSub style={{ marginTop: '1rem', fontSize: '1.02rem' }}>
              Not sure which tier is the right fit? Click <b>Contact Sales</b> and we’ll help you choose the best plan for your workflow.
            </HeroSub>

            <BadgeRow>
              <Badge>
                <FiHardDrive className="icon" />
                <div className="label">High-capacity storage (up to 50TB)</div>
              </Badge>
              <Badge>
                <FiLink className="icon" />
                <div className="label">Advanced sharing</div>
              </Badge>
              <Badge>
                <FiRotateCcw className="icon" />
                <div className="label">Version control</div>
              </Badge>
              <Badge>
                <FiSmartphone className="icon" />
                <div className="label">Mobile access</div>
              </Badge>
              <Badge>
                <FiHeadphones className="icon" />
                <div className="label">24/7 support</div>
              </Badge>
              <Badge>
                <FiZap className="icon" />
                <div className="label">Integrated workflow</div>
              </Badge>
            </BadgeRow>
          </Hero>

          <Section>
            <PageHeader title="Why Choose RedCloud to store your files" highlightWord="Cloud" />
          </Section>

          <Section>
            <SectionTitle>The problem: consumer cloud is great… until you <span>run out of storage</span></SectionTitle>
            <TwoCol>
              <Card>
                <BodyText>
                  Consumer cloud plans are awesome for everyday files. When your library grows into multiple terabytes, friction starts to show:
                  tier ceilings, messy project organization, awkward client delivery, and unclear costs.
                </BodyText>
              </Card>
              <Card>
                <BodyText>
                  Creators need a single solution that works for both archives and active projects, with simple sharing links for clients and teams,
                  predictable pricing, and support from humans. Most big tech plans are optimized for average users—not libraries of content for creators.
                </BodyText>
              </Card>
            </TwoCol>
          </Section>

      <Section>
        <SectionTitle>Comparison snapshot: what each tool is great at—and where it can get tricky</SectionTitle>
        <BodyText>
          We’re fans of a lot of these platforms. The difference is that RedCloud is intentionally designed for multi-terabyte creator libraries.
        </BodyText>
        <CompetitorGrid>
          {competitors.map((c) => (
            <CompetitorCard key={c.name}>
              <h3>{c.name}</h3>
              <div className="row">
                <div className="label">Best for</div>
                <div className="value">{c.bestFor}</div>
              </div>
              <div className="row">
                <div className="label">Not ideal for</div>
                <div className="value">{c.notIdealFor}</div>
              </div>
              <FitPill>
                Creator Fit (10TB+): <span>{c.fit}</span>
              </FitPill>
            </CompetitorCard>
          ))}
        </CompetitorGrid>
      </Section>

      <Section>
        <SectionTitle>How RedCloud compares when you actually need multi-TB storage.</SectionTitle>
        <ComparisonGrid>
          {rows.map((r) => (
            <ComparisonCard key={r.provider}>
              <h3>{r.provider.startsWith('RedCloud') ? <span className="red">{r.provider}</span> : r.provider}</h3>
              <p className="sub">{r.tier}</p>
              <div className="row">
                <div className="label">Approx monthly price</div>
                <div className="value">{r.price}</div>
              </div>
              <div className="row">
                <div className="label">Approx $/TB/month</div>
                <div className="value">{r.perTb}</div>
              </div>
              <div className="row">
                <div className="label">Best for</div>
                <div className="value">{r.bestFor}</div>
              </div>
              <div className="row">
                <div className="label">Limits / notes</div>
                <div className="value">{r.notes}</div>
              </div>
            </ComparisonCard>
          ))}
        </ComparisonGrid>
        <Disclaimer>
          * Prices shown are common US monthly rates and simplified estimates; provider pricing and terms can change.
          Verify current pricing/features on vendor websites.
        </Disclaimer>
      </Section>

      <Section>
        <SectionTitle>Why our pricing makes sense</SectionTitle>
        <TwoCol>
          <Card>
            <BodyText>
              We’re targeting creators who need multi-terabyte storage in one place. That’s a different world than “a few folders plus phone backups.”
              Our Growth/Studio/Pro tiers are priced close to the market’s ~ $5/TB baseline you often see in big consumer plans.
            </BodyText>
          </Card>
          <Card>
            <BodyText>
              The 3TB Starter costs more per TB because it includes premium support + pro features, and because reliably hosting multi-TB
              accounts has real infrastructure costs. It’s designed for serious creators and naturally encourages scaling to 10TB when you need it.
            </BodyText>
          </Card>
        </TwoCol>
      </Section>

      <Section>
        <SectionTitle>About monthly transfer (bandwidth)</SectionTitle>
        <Card>
          <BodyText>
            Each plan includes an <b>Included monthly transfer</b> allowance (250GB / 500GB / 750GB / 1TB). This helps keep performance predictable
            for all customers and prevents a small number of extreme-transfer cases from impacting everyone. Transfer policies like this are common
            behind the scenes—even if some providers don’t label them.
          </BodyText>
          <BodyText style={{ marginTop: '0.75rem' }}>
            If you consistently hit your Included monthly transfer, we can review your usage and discuss options on a case-by-case basis.
            This isn’t a standard upgrade for everyone—our goal is to keep performance predictable for all customers.
          </BodyText>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Where RedCloud wins for creators</SectionTitle>
        <BulletGrid>
          <BulletCard>
            <h3>High-capacity storage built for real libraries</h3>
            <p>Pick 10TB–50TB without stitching together multiple consumer accounts.</p>
          </BulletCard>
          <BulletCard>
            <h3>Share links that feel professional</h3>
            <p>Password, expiry, view-only options, and controlled access for clients and collaborators.</p>
          </BulletCard>
          <BulletCard>
            <h3>Version control</h3>
            <p>Undo accidents, recover previous versions, and keep projects sane over time.</p>
          </BulletCard>
          <BulletCard>
            <h3>Mobile access</h3>
            <p>Your library stays reachable when you’re on set, on-site, or on the road.</p>
          </BulletCard>
          <BulletCard>
            <h3>Human support that understands creator workflows</h3>
            <p>Not just ticket macros—support that gets how projects, clients, and deadlines work.</p>
          </BulletCard>
          <BulletCard>
            <h3>Creator-friendly onboarding</h3>
            <p>Help migrating from external drives, local NAS, or scattered accounts into one clean archive.</p>
          </BulletCard>
          <BulletCard>
            <h3>Better for archive + backup than gallery platforms</h3>
            <p>Galleries are for delivery; RedCloud is for long-term retention and controlled sharing.</p>
          </BulletCard>
          <BulletCard>
            <h3>Designed to pair well with your existing stack</h3>
            <p>Use RedCloud as the master archive while you keep your editing and delivery tools.</p>
          </BulletCard>
        </BulletGrid>

        <Section style={{ marginTop: '1rem' }}>
          <Callout>
            <h3>Gallery platforms vs RedCloud (they’re not the same thing)</h3>
            <p>
              Gallery platforms are amazing for client presentation and delivery. RedCloud is designed for long-term archive + high-capacity storage + controlled sharing.
              Many studios use both: a gallery tool for delivery, RedCloud for the master archive.
            </p>
          </Callout>
        </Section>
      </Section>

      <Section>
        <SectionTitle>Use cases</SectionTitle>
        <UseCaseGrid>
          <UseCaseCard>
            <h3>Photographers</h3>
            <div className="meta">RAW archives, weddings, multi-year retention, and quick client handoff links.</div>
            <div className="meta" style={{ marginTop: '0.5rem' }}><b>Typical library size:</b> 5TB, 12TB, 30TB</div>
            <div className="tier">Recommended tier: <span>Growth (10TB)</span> or <span>Studio (25TB)</span></div>
          </UseCaseCard>
          <UseCaseCard>
            <h3>Videographers</h3>
            <div className="meta">4K/6K footage, project folders, team sharing, and version restore for production mistakes.</div>
            <div className="meta" style={{ marginTop: '0.5rem' }}><b>Typical library size:</b> 8TB, 18TB, 45TB</div>
            <div className="tier">Recommended tier: <span>Studio (25TB)</span> or <span>Pro (50TB)</span></div>
          </UseCaseCard>
          <UseCaseCard>
            <h3>Content Creators</h3>
            <div className="meta">Multi-platform video assets, thumbnails, project timelines, brand kits, and fast share links for editors and collaborators.</div>
            <div className="meta" style={{ marginTop: '0.5rem' }}><b>Typical library size:</b> 4TB, 10TB, 25TB</div>
            <div className="tier">Recommended tier: <span>Growth (10TB)</span> or <span>Studio (25TB)</span></div>
          </UseCaseCard>
          <UseCaseCard>
            <h3>Musicians & audio creators</h3>
            <div className="meta">Multitrack sessions, stems, masters, sample libraries, and share links for collaborators.</div>
            <div className="meta" style={{ marginTop: '0.5rem' }}><b>Typical library size:</b> 2TB, 6TB, 15TB</div>
            <div className="tier">Recommended tier: <span>Growth (10TB)</span> or <span>Studio (25TB)</span></div>
          </UseCaseCard>
        </UseCaseGrid>
      </Section>

      <Section>
        <SectionTitle>Proof without the hype</SectionTitle>
        <TwoCol>
          <Card>
            <SectionTitle style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}><span>Built by infrastructure people</span></SectionTitle>
            <BodyText>Designed for uptime, reliability, and the boring-but-important work of keeping your library safe.</BodyText>
          </Card>
          <Card>
            <SectionTitle style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}><span>Transparent pricing</span></SectionTitle>
            <BodyText>No mystery “contact sales for storage” when you hit real size. Pick a tier, know your cost, and scale when you need to.</BodyText>
          </Card>
          <Card>
            <SectionTitle style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}><span>Free trial to prove it</span></SectionTitle>
            <BodyText>Start with 50GB for 30 days and validate the workflow before you move your archive.</BodyText>
          </Card>
          <Card>
            <SectionTitle style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}><span>Explore features</span></SectionTitle>
            <BodyText>
              Want a deeper breakdown? See our <Link to="/features" style={{ color: '#ff3333', textDecoration: 'none', fontWeight: 600 }}>features</Link> and how sharing, version restore, and mobile access fit together.
            </BodyText>
          </Card>
        </TwoCol>
      </Section>

      <Section>
        <SectionTitle>FAQ</SectionTitle>
        <FAQWrap>
          <FAQItem>
            <summary>Is RedCloud cheaper than Google Drive/iCloud?</summary>
            <p>
              It depends on the tier and what you’re comparing. Big consumer plans can look very cheap per TB at smaller sizes.
              RedCloud is optimized for creators who want 10TB–50TB in one place with professional sharing, version restore, and real support.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>Why do you offer 10TB–50TB plans?</summary>
            <p>
              Because creator libraries grow fast: RAW + video + deliverables + backups. RedCloud is designed around multi-terabyte reality,
              so you don’t have to split archives across accounts or tools.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>What does “Included monthly transfer” mean?</summary>
            <p>
              It’s the amount of data transfer included each month with your plan (250GB / 500GB / 750GB / 1TB). It keeps performance predictable
              and helps prevent extreme-transfer cases from affecting other customers.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>Can I get more transfer if I need it?</summary>
            <p>
              Sometimes. If you’re consistently hitting your Included monthly transfer, we can review your account and workflow and adjust on a case-by-case basis.
              It’s not a general “upgrade option” and it isn’t guaranteed for every use case.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>Are gallery platforms the same thing as RedCloud?</summary>
            <p>
              Not really. Gallery platforms are built for galleries and client delivery. RedCloud is built for long-term archive + high-capacity storage + controlled sharing.
              Many studios use both.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>Do you support sharing links with passwords/expiry?</summary>
            <p>
              Yes—RedCloud supports professional share links with controls like passwords and expiration so you can share confidently with clients and teams.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>What happens if I cancel—can I export my data?</summary>
            <p>
              You can export your data. If you’re planning a migration, talk to us—we’ll help you do it cleanly and safely.
            </p>
          </FAQItem>
          <FAQItem>
            <summary>Is my data private and secure?</summary>
            <p>
              We take privacy and security seriously with modern infrastructure practices and access controls.
              If you have compliance needs or specific questions, contact us and we’ll walk through the details.
            </p>
          </FAQItem>
        </FAQWrap>
      </Section>

      <FinalCTA>
        <h2>Stop fighting storage limits. Start building.</h2>
        <p>
          Try RedCloud with a free 50GB trial for 30 days, or compare tiers and pick the right fit for your library.
        </p>
        <CTAGroup style={{ justifyContent: 'center' }}>
          <PrimaryCTA href="https://my.redcloudstorage.net" target="_blank" rel="noopener noreferrer">
            Start Free Trial
          </PrimaryCTA>
          <SecondaryCTA to="/pricing">View Pricing</SecondaryCTA>
        </CTAGroup>
      </FinalCTA>
        </CloudText>
      </ContentContainer>
    </Page>
  );
}

export default WhyRedCloud;
